import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { cx } from '@emotion/css';
import { DSButton } from '@stihl-design-system/components';

import { newrelic } from '@phoenix/core/frontend/shared/ui/newrelic/newrelic';
import { useToast } from '@phoenix/design-system/frontend/ui/notifications/useToast';
import * as GATracking from '@phoenix/core/frontend/shared/ui/feedback/feedback.ga';
import { useUserSettings } from '@phoenix/team-m/dashboard/frontend/shared/use-user-settings';
import BaseBanner from '@phoenix/team-m/dashboard/frontend/shared/components/base-banner';

import { FeedbackModal } from './feedback-modal';

import styles from '../overview/dashboard-overview.module.scss';

export const FeedbackBanner = ({ isFirstSection = false, isLastSection = false }) => {
  const { t } = useTranslation();
  const { showSuccessNotification } = useToast();
  const [showSection, setShowSection] = useState(false);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [data, mutateSettingService] = useUserSettings('dashboard.panel.feedback');

  useEffect(() => {
    if (data) {
      setShowSection(!data.userSetting?.setting?.feedbackSent);
    }
  }, [data]);

  const onSubmitFeedback = async (value: string) => {
    setDialogIsOpen(false);
    newrelic.addPageAction('feedback', {
      context: 'dashboard.banner',
      triggerType: 'button',
      triggerName: 'submit',
      triggerAction: 'click',
      value,
    });
    void GATracking.trackFeedbackSubmission({
      eventName: 'mystihl_user_feedback',
      context: 'dashboard.banner',
      question: t('dashboard_feedbackBanner_popup_subHeadline'),
      feedbackText: value,
    });
    const mutationResponse = await mutateSettingService({ feedbackSent: true });

    if (mutationResponse?.data?.createUserSetting?.setting?.feedbackSent) {
      showSuccessNotification(t('dashboard_feedbackBanner_toast_success'));
      setShowSection(false);
    }
  };

  const onCloseDialog = () => {
    setDialogIsOpen(false);
  };

  return showSection ? (
    <>
      <BaseBanner
        id="feedback-banner"
        backgroundImg="tree-trunks-cut.webp"
        imageTags={['imagesDashboard']}
        headline={t('dashboard_feedbackBanner_headline')}
        description={t('dashboard_feedbackBanner_subHeadline')}
        className={cx('relative py-11', {
          [styles.bannerFirst]: isFirstSection,
          [styles.bannerLast]: isLastSection,
        })}
      >
        <DSButton
          type="button"
          onClick={() => {
            setDialogIsOpen(true);
          }}
          className="mt-5 w-full tablet:w-fit"
          aria-haspopup="dialog"
          data-testid="feedback-banner-button"
        >
          {t('dashboard_feedbackBanner_button_letUsKnow')}
        </DSButton>
      </BaseBanner>
      <FeedbackModal dialogIsOpen={dialogIsOpen} onClose={onCloseDialog} onSubmit={onSubmitFeedback} />
    </>
  ) : null;
};
